import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatSession, DifyConversation } from '@/types/chat';
import { useChatApi } from '@/hooks/useChatApi';
import { ChatApiService } from '@/services/chatApiService';

const CACHE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes

export function useChatSessions(
  chatApiService: ChatApiService,
  opts: { userId: string | null; isUserLoading: boolean }
) {
  const { t } = useTranslation();
  const chatApi = useChatApi(chatApiService);

  // User State
  const [userId, setUserId] = useState<string | null>(opts.userId ?? null);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(opts.isUserLoading ?? true);

  // Session State
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  
  // Title Editing State
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingChatTitle, setEditingChatTitle] = useState<string>('');
  
  // Pending Rename State
  const [pendingRename, setPendingRename] = useState<{ chatId: string; newTitle: string } | null>(null);
  const [processingRenames, setProcessingRenames] = useState<Set<string>>(new Set());

  // Internal State
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [isChatInitialized, setIsChatInitialized] = useState<boolean>(false);

  const chatSessionsRef = useRef(chatSessions);
  useEffect(() => {
    chatSessionsRef.current = chatSessions;
  }, [chatSessions]);

  useEffect(() => {
    setSelectedChatIds((prev) =>
      prev.filter((chatId) =>
        chatSessionsRef.current.some((session) => session.id === chatId)
      )
    );
  }, [chatSessions]);

  // --- Sync external auth state into local state ---
  useEffect(() => {
    setUserId(opts.userId ?? null);
    setIsUserLoading(!!opts.isUserLoading);
  }, [opts.userId, opts.isUserLoading]);

  // --- Cache Management ---
  const getDeletedChatIds = useCallback((): string[] => {
    if (typeof window === 'undefined') return [];
    const deletedChatIdsString = localStorage.getItem('deleted-chat-ids') || '[]';
    try {
      return JSON.parse(deletedChatIdsString);
    } catch (e) {
      console.error("Error parsing deleted chat IDs:", e);
      return [];
    }
  }, []);

  const addDeletedChatId = useCallback((chatId: string) => {
    if (typeof window === 'undefined') return;
    const deletedIds = getDeletedChatIds();
    if (!deletedIds.includes(chatId)) {
      deletedIds.push(chatId);
      localStorage.setItem('deleted-chat-ids', JSON.stringify(deletedIds));
    }
  }, [getDeletedChatIds]);

  const loadConversationsFromCache = useCallback((): ChatSession[] => {
      if (typeof window === 'undefined' || !userId) return [];
      const conversationsCacheKey = `dify-conversations-${userId}`;
      const cachedConversationsString = localStorage.getItem(conversationsCacheKey);
      const deletedChatIds = getDeletedChatIds();

      if (cachedConversationsString) {
          try {
              const parsedSessions = JSON.parse(cachedConversationsString);
              if (!Array.isArray(parsedSessions)) {
                  console.error("Invalid cached conversations format: not an array.");
                  localStorage.removeItem(conversationsCacheKey);
                  return [];
              }
              const sessions = parsedSessions.map((sessionData: any) => ({
                  ...sessionData,
                  createdAt: new Date(sessionData.createdAt),
                  updatedAt: new Date(sessionData.updatedAt),
                  messages: [], // Messages loaded separately
                  files: sessionData.files || [],
                  userId: sessionData.userId || userId,
              })).filter((session: ChatSession) => !deletedChatIds.includes(session.id));
              console.log(`[useChatSessions] Loaded ${sessions.length} sessions from cache.`);
              return sessions;
          } catch (e) {
              console.error("Error parsing cached conversations:", e);
              localStorage.removeItem(conversationsCacheKey);
          }
      }
      return [];
  }, [userId, getDeletedChatIds]);

  const saveConversationsToCache = useCallback((sessions: ChatSession[]) => {
      if (typeof window === 'undefined' || !userId) return;
      const conversationsCacheKey = `dify-conversations-${userId}`;
      try {
          const sessionsToCache = sessions.map(session => ({
              ...session,
              messages: [],
              createdAt: session.createdAt.toISOString(),
              updatedAt: session.updatedAt.toISOString(),
          }));
          localStorage.setItem(conversationsCacheKey, JSON.stringify(sessionsToCache));
      } catch (e) {
          console.error("Error saving conversations to cache:", e);
      }
  }, [userId]);

  const clearChatCache = useCallback((chatId: string, difyConversationId?: string) => {
      if (typeof window === 'undefined' || !userId) return;
      const conversationsCacheKey = `dify-conversations-${userId}`;
      localStorage.removeItem(`dify-messages-${chatId}-${userId}`);

      if (difyConversationId) {
          localStorage.removeItem(`dify-messages-${difyConversationId}-${userId}`);
          localStorage.removeItem(`message-visibility-${difyConversationId}-${userId}`);
      }

      const cachedConversationsString = localStorage.getItem(conversationsCacheKey);
      if (cachedConversationsString) {
          try {
              let cachedConversations = JSON.parse(cachedConversationsString);
              cachedConversations = cachedConversations.filter((session: any) => session.id !== chatId);
              localStorage.setItem(conversationsCacheKey, JSON.stringify(cachedConversations));
          } catch (e) {
              console.error("Error updating conversation cache after deletion:", e);
              localStorage.removeItem(conversationsCacheKey);
          }
      }
  }, [userId]);

  // --- Core Session Management ---
  const updateChatSession = useCallback((chatId: string, updates: Partial<ChatSession>) => {
    setChatSessions(prev =>
      prev.map(chat =>
        chat.id === chatId ? { ...chat, ...updates, updatedAt: new Date() } : chat
      )
    );
  }, []);

  // --- Initialization Effect ---
  useEffect(() => {
    if (!userId || isChatInitialized) return;

    const initializeChatbot = async () => {
      setIsLoading(true);
      setIsChatInitialized(true);
      try {
        const deletedChatIds = getDeletedChatIds();
        let initialSessions = loadConversationsFromCache();
        if (JSON.stringify(initialSessions) !== JSON.stringify(chatSessionsRef.current.filter(s => !s.messages.length))) {
            setChatSessions(initialSessions);
        }

        const currentTime = Date.now();
        if (initialSessions.length === 0 || (currentTime - lastFetchTime) > CACHE_EXPIRATION_TIME || pendingRename) {
          const apiData = await chatApi.fetchConversations(userId);
          let fetchedConversations = Array.isArray(apiData?.data) ? apiData.data : [];

          fetchedConversations = fetchedConversations.filter((conv: DifyConversation) => {
            const chatId = `chat${conv.created_at * 1000}-${conv.id}`;
            return !deletedChatIds.includes(chatId);
          });

          const newSessionsFromFetch: ChatSession[] = fetchedConversations.map((conv: DifyConversation) => ({
            id: `chat${conv.created_at * 1000}-${conv.id}`,
            difyConversationId: conv.id,
            title: conv.name,
            messages: [],
            createdAt: new Date(conv.created_at * 1000),
            updatedAt: new Date(conv.updated_at * 1000),
            files: [],
            userId,
          }));

          const currentSessions = chatSessionsRef.current;
          const mergedSessionsMap = new Map<string, ChatSession>();
          currentSessions.forEach(session => mergedSessionsMap.set(session.id, session));
          newSessionsFromFetch.forEach(session => {
            let found = false;
            for (const existing of mergedSessionsMap.values()) {
              if (existing.difyConversationId && existing.difyConversationId === session.difyConversationId) {
                mergedSessionsMap.set(existing.id, { ...existing, title: session.title, updatedAt: session.updatedAt > existing.updatedAt ? session.updatedAt : existing.updatedAt, messages: existing.messages || [] });
                found = true;
                break;
              }
            }
            if (!found) {
                 const existingById = mergedSessionsMap.get(session.id);
                 if (existingById) {
                     mergedSessionsMap.set(session.id, { ...existingById, difyConversationId: session.difyConversationId, title: session.title, updatedAt: session.updatedAt > existingById.updatedAt ? session.updatedAt : existingById.updatedAt, messages: existingById.messages || [] });
                 } else {
                     mergedSessionsMap.set(session.id, session);
                 }
            }
          });

          const mergedSessions = Array.from(mergedSessionsMap.values());
          const sortedSessions = mergedSessions.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

          setChatSessions(sortedSessions);
          saveConversationsToCache(sortedSessions);
          setLastFetchTime(currentTime);
        } else {
            console.log("[useChatSessions] Using cached conversations.");
             if (JSON.stringify(initialSessions) !== JSON.stringify(chatSessionsRef.current.filter(s => !s.messages.length))) {
                setChatSessions(initialSessions);
             }
        }
      } catch (error) {
        console.error("Error initializing chatbot:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChatbot();
  }, [userId, isChatInitialized, chatApi, getDeletedChatIds, loadConversationsFromCache, saveConversationsToCache, lastFetchTime, pendingRename]);

  // --- Pending Rename Effect ---
  useEffect(() => {
    const activeChat = chatSessions.find(c => c.id === activeChatId);
    if (pendingRename && userId && activeChat && pendingRename.chatId === activeChat.id && activeChat.difyConversationId && !processingRenames.has(activeChat.id)) {
        const applyPendingRename = async () => {
            console.log("Applying pending rename for chatId:", activeChat.id);
            try {
                setProcessingRenames(prev => new Set(prev).add(activeChat.id!));
                await chatApi.renameConversation(activeChat.difyConversationId!, pendingRename.newTitle, userId);
                setPendingRename(null);
                updateChatSession(activeChat.id!, { title: pendingRename.newTitle });
                saveConversationsToCache(chatSessionsRef.current.map(s => s.id === activeChat.id ? {...s, title: pendingRename.newTitle} : s));
            } catch (error) {
                console.error("Failed to apply pending rename:", error);
            } finally {
                setProcessingRenames(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(activeChat.id!);
                    return newSet;
                });
            }
        };
        applyPendingRename();
    }
  }, [activeChatId, chatSessions, pendingRename, userId, chatApi, processingRenames, updateChatSession, saveConversationsToCache]);

  // --- Actions ---
  const createNewChat = useCallback(async (): Promise<string | null> => {
    let effectiveUserId = userId;
    if (!effectiveUserId && typeof window !== 'undefined') {
      const m = window.location.pathname.match(/^\/chat\/([^/?#]+)/);
      if (m && m[1]) effectiveUserId = m[1];
    }
    if (!effectiveUserId) {
        console.error("[createNewChat] Cannot create chat: effective userId not available.");
        alert(t('error_creating_chat_user'));
        return null;
    }

    const newChatId = `chat${Date.now()}`;
    const newChat: ChatSession = {
      id: newChatId,
      difyConversationId: undefined,
      title: t('new_chat_title'),
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      files: [],
      userId: effectiveUserId,
    };

    const updatedSessions = [newChat, ...chatSessionsRef.current];
    chatSessionsRef.current = updatedSessions;
    setChatSessions(updatedSessions);
    setActiveChatId(newChat.id);
    saveConversationsToCache(updatedSessions);
    return newChat.id;
  }, [userId, t, saveConversationsToCache]);

  const setSelectionMode = useCallback((value: boolean) => {
    setIsSelectionMode(value);
    if (!value) {
      setSelectedChatIds([]);
    }
  }, []);

  const clearSelectedChats = useCallback(() => {
    setSelectedChatIds([]);
  }, []);

  const toggleChatSelection = useCallback((chatId: string) => {
    setSelectedChatIds((prev) => {
      if (prev.includes(chatId)) {
        return prev.filter((id) => id !== chatId);
      }
      return [...prev, chatId];
    });
  }, []);

  const selectAllChats = useCallback((chatIds?: string[]) => {
    const idsToSelect =
      chatIds && chatIds.length > 0
        ? chatIds
        : chatSessionsRef.current.map((chat) => chat.id);
    setSelectedChatIds(idsToSelect);
    if (!isSelectionMode) {
      setIsSelectionMode(true);
    }
  }, [isSelectionMode]);

  const deleteChats = useCallback(
    async (chatIds: string[]) => {
      if (!userId || chatIds.length === 0) return;

      const chatsToDelete = chatSessionsRef.current.filter((chat) =>
        chatIds.includes(chat.id)
      );
      if (chatsToDelete.length === 0) return;

      const updatedSessions = chatSessionsRef.current.filter(
        (chat) => !chatIds.includes(chat.id)
      );
      setChatSessions(updatedSessions);

      if (activeChatId && chatIds.includes(activeChatId)) {
        setActiveChatId(
          updatedSessions.length > 0 ? updatedSessions[0].id : null
        );
      }

      chatIds.forEach((chatId) => {
        const chatToDelete = chatsToDelete.find((chat) => chat.id === chatId);
        if (!chatToDelete) return;
        addDeletedChatId(chatId);
        clearChatCache(chatId, chatToDelete.difyConversationId);
      });

      setSelectedChatIds((prev) =>
        prev.filter((chatId) => !chatIds.includes(chatId))
      );
    },
    [
      userId,
      activeChatId,
      addDeletedChatId,
      clearChatCache,
      setChatSessions,
      setActiveChatId,
    ]
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      await deleteChats([chatId]);
    },
    [deleteChats]
  );

  const deleteSelectedChats = useCallback(async () => {
    await deleteChats(selectedChatIds);
  }, [deleteChats, selectedChatIds]);

  const startEditingTitle = useCallback((chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditingChatTitle(currentTitle);
  }, []);

  const cancelEditingTitle = useCallback(() => {
    setEditingChatId(null);
    setEditingChatTitle('');
  }, []);

  const saveChatTitle = useCallback(async (chatId: string) => {
    const newTitle = editingChatTitle.trim();
    if (!newTitle || !editingChatId || editingChatId !== chatId || !userId) {
        cancelEditingTitle();
        return;
    }

    const chatSession = chatSessionsRef.current.find(chat => chat.id === chatId);
    if (!chatSession || chatSession.title === newTitle) {
        cancelEditingTitle();
        return;
    }

    updateChatSession(chatId, { title: newTitle });
    saveConversationsToCache(chatSessionsRef.current.map(s => s.id === chatId ? {...s, title: newTitle} : s));
    cancelEditingTitle();

    if (chatSession.difyConversationId) {
      try {
        await chatApi.renameConversation(chatSession.difyConversationId, newTitle, userId);
        if (pendingRename?.chatId === chatId) {
          setPendingRename(null);
        }
      } catch (error) {
        console.error("Error renaming conversation on Dify, setting as pending:", error);
        setPendingRename({ chatId, newTitle });
      }
    } else {
      setPendingRename({ chatId, newTitle });
    }
  }, [editingChatTitle, editingChatId, userId, chatApi.renameConversation, updateChatSession, saveConversationsToCache, cancelEditingTitle, pendingRename]);

  const openLastConversation = useCallback(() => {
    const sessions = chatSessionsRef.current || [];
    if (!sessions.length) return;

    const sorted = [...sessions].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    const newest = sorted[0];
    if (!newest) return;

    const isNewestEmptyActive =
      newest.id === activeChatId &&
      (!newest.difyConversationId || newest.difyConversationId.trim() === '') &&
      (!newest.messages || newest.messages.length === 0);

    let target = newest;

    if (isNewestEmptyActive) {
      const lastReal = sorted.find(session => (
        session.id !== newest.id &&
        ((session.difyConversationId && session.difyConversationId.trim() !== '') ||
          (session.messages && session.messages.length > 0))
      ));

      if (lastReal) {
        target = lastReal;
      }
    }

    if (!target) return;

    setActiveChatId(target.id);
  }, [activeChatId, setActiveChatId, chatSessionsRef]);

  return {
    // User
    userId,
    isUserLoading,
    // Sessions
    chatSessions,
    setChatSessions,
    activeChatId,
    setActiveChatId,
    isLoading,
    updateChatSession,
    createNewChat,
    deleteChat,
    deleteSelectedChats,
    saveConversationsToCache, // Exposing for other hooks
    chatSessionsRef, // Exposing for other hooks
    // Selection
    isSelectionMode,
    setSelectionMode,
    selectedChatIds,
    toggleChatSelection,
    selectAllChats,
    clearSelectedChats,
    // Title Editing
    startEditingTitle,
    cancelEditingTitle,
    saveChatTitle,
    editingChatId,
    editingChatTitle,
    setEditingChatTitle,
    openLastConversation,
  };
}